import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';

export function Footer() {
  const [pagesOpen, setPagesOpen] = useState(false);

  return (
    <footer className="relative z-10 w-full border-t border-border/40">
      <div className="mx-auto max-w-6xl px-6 py-5 flex flex-col-reverse items-center gap-4 sm:flex-row sm:justify-between">
        {/* Copyright */}
        <p className="text-center text-sm text-muted-foreground sm:text-left">
          &copy; {new Date().getFullYear()} Anime Draft. A free, non-profit fan project, not affiliated with any studio, publisher or rights holder. All characters belong to their owners. No copyright infringement intended.
        </p>

        {/* Right side: icons + Pages */}
        <div className="flex items-center gap-3">
          {/* Discord */}
          <a
            href="https://discord.gg/Yet6UyaN4e"
            target="_blank"
            rel="noreferrer"
            aria-label="Join Discord"
            className="flex items-center justify-center w-10 h-10 rounded-full bg-[#5865F2]/15 border border-[#5865F2]/30 text-[#5865F2] hover:bg-[#5865F2]/25 transition-colors"
          >
            <svg viewBox="0 0 24 24" aria-hidden="true" className="h-5 w-5">
              <path
                fill="currentColor"
                d="M19.55 5.39a17 17 0 0 0-4.21-1.3c-.18.33-.39.77-.53 1.11a16 16 0 0 0-4.58 0c-.14-.34-.35-.78-.53-1.11a17 17 0 0 0-4.22 1.3C2.94 8.15 2 10.84 2 13.5a17 17 0 0 0 5.55 2.79c.43-.6.82-1.24 1.13-1.91a10.8 10.8 0 0 1-1.78-.86c.15-.11.3-.22.43-.34a11.2 11.2 0 0 0 9.34 0c.14.12.28.23.43.34-.56.32-1.15.6-1.78.86.31.67.7 1.31 1.13 1.91A16.9 16.9 0 0 0 22 13.5c0-2.66-.94-5.35-2.45-8.11ZM9.6 13.46c-.66 0-1.2-.61-1.2-1.36 0-.75.53-1.36 1.2-1.36s1.2.61 1.2 1.36c0 .75-.53 1.36-1.2 1.36Zm4.8 0c-.66 0-1.2-.61-1.2-1.36 0-.75.54-1.36 1.2-1.36.67 0 1.2.61 1.2 1.36 0 .75-.53 1.36-1.2 1.36Z"
              />
            </svg>
          </a>

          {/* Ko-fi */}
          <a
            href="https://ko-fi.com/animedraft"
            target="_blank"
            rel="noreferrer"
            aria-label="Support on Ko-fi"
            className="flex items-center justify-center w-10 h-10 rounded-full bg-[#18A6E3]/15 border border-[#18A6E3]/30 text-[#18A6E3] hover:bg-[#18A6E3]/25 transition-colors"
          >
            <svg viewBox="0 0 24 24" aria-hidden="true" className="h-5 w-5">
              <path
                fill="currentColor"
                d="M4 4h12a4 4 0 0 1 0 8h-1.2l-1.6 7.2a2 2 0 0 1-1.96 1.56H8.76a2 2 0 0 1-1.96-1.56L4 4Zm12 6a2 2 0 0 0 0-4h-1.1l.9 4H16Z"
              />
            </svg>
          </a>

          {/* Reddit */}
          <a
            href="https://www.reddit.com/r/Anime_Draft/"
            target="_blank"
            rel="noreferrer"
            aria-label="Join Reddit"
            className="flex items-center justify-center w-10 h-10 rounded-full bg-[#FF4500]/15 border border-[#FF4500]/30 text-[#FF4500] hover:bg-[#FF4500]/25 transition-colors"
          >
            <svg viewBox="0 0 24 24" aria-hidden="true" className="h-5 w-5">
              <circle cx="9.25" cy="13" r="1.1" fill="currentColor" />
              <circle cx="14.75" cy="13" r="1.1" fill="currentColor" />
              <path d="M9 15.6c.9.65 2 .95 3 .95s2.1-.3 3-.95" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
              <path d="M7.2 14.5c-.45-.4-.7-.93-.7-1.5 0-.78.47-1.45 1.16-1.76A4.9 4.9 0 0 1 12 9a4.9 4.9 0 0 1 4.34 2.24c.69.31 1.16.98 1.16 1.76 0 .57-.25 1.1-.7 1.5" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
              <path d="M13.1 8.55l.6-2.35 1.95.45" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
              <circle cx="16.05" cy="6.55" r="1.15" fill="none" stroke="currentColor" strokeWidth="1.4" />
            </svg>
          </a>

          {/* Pages popover */}
          <Popover open={pagesOpen} onOpenChange={setPagesOpen}>
            <PopoverTrigger asChild>
              <button
                className="px-5 py-2 rounded-full text-sm font-display font-semibold text-primary-foreground bg-primary hover:bg-primary/90 transition-colors"
              >
                Pages
              </button>
            </PopoverTrigger>
            <PopoverContent
              align="end"
              sideOffset={8}
              className="w-48 p-2 bg-card border-border"
            >
              <nav className="flex flex-col gap-1">
                <Link
                  to="/how-it-works"
                  onClick={() => setPagesOpen(false)}
                  className="px-3 py-2 text-sm rounded-lg hover:bg-secondary/60 text-muted-foreground hover:text-foreground transition-colors"
                >
                  How It Works
                </Link>
                <Link
                  to="/faq"
                  onClick={() => setPagesOpen(false)}
                  className="px-3 py-2 text-sm rounded-lg hover:bg-secondary/60 text-muted-foreground hover:text-foreground transition-colors"
                >
                  FAQ
                </Link>
                <Link
                  to="/contact"
                  onClick={() => setPagesOpen(false)}
                  className="px-3 py-2 text-sm rounded-lg hover:bg-secondary/60 text-muted-foreground hover:text-foreground transition-colors"
                >
                  Contact Me
                </Link>
                <Link
                  to="/privacy"
                  onClick={() => setPagesOpen(false)}
                  className="px-3 py-2 text-sm rounded-lg hover:bg-secondary/60 text-muted-foreground hover:text-foreground transition-colors"
                >
                  Privacy Policy
                </Link>
                <Link
                  to="/terms"
                  onClick={() => setPagesOpen(false)}
                  className="px-3 py-2 text-sm rounded-lg hover:bg-secondary/60 text-muted-foreground hover:text-foreground transition-colors"
                >
                  Terms of Service
                </Link>
              </nav>
            </PopoverContent>
          </Popover>
        </div>
      </div>
    </footer>
  );
}
